import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import { Theme } from '@mui/material/styles';
import { ExternalLink, X } from 'lucide-react';
import { FilePath } from '@askporter/client-grieg-lyric';
import { AllowedFileTypes, getFileDetails } from '@askporter/utils';
import { Button } from '../../../Buttons/Button';
import { IconButton } from '../../../Buttons/IconButton';
import { Typography } from '../../../Typography';
import { getPreviewFileName } from './utils';
/** Height of the document region. The card stays the same size whatever the document does inside it. */
const DOCUMENT_HEIGHT = '420px';
const DOCUMENT_HEIGHT_SMALL = '320px';
/** Height of the image region, so a portrait photo cannot push the card past the viewport. */
const IMAGE_HEIGHT = '480px';
/**
 * PDFium conventions Chrome and Edge honour to drop the thumbnail rail and open on page one. Firefox and Safari
 * ignore them and keep their own toolbar, which the fixed-height region is designed to accommodate. The fragment
 * goes after the signed-URL query string, which is safe — a fragment is never sent to the server, so signature
 * validation does not see it.
 */
const VIEWER_CHROME_FRAGMENT = '#navpanes=0&toolbar=0&scrollbar=0&view=FitH&page=1';
/**
 * A browser that hands the PDF to a native viewer, or is served it as an attachment, navigates the frame nowhere
 * and fires no load event. Without a deadline the user is left looking at an empty white frame.
 */
const PREVIEW_TIMEOUT_MS = 5000;
export interface FilePreviewProps {
  /**
   * Whether the parent is rendered below the `sm` breakpoint — collapses the action to an icon and shortens the
   * document region
   */
  isSmallDevice?: boolean;
  /**
   * Closes the modal — also called on backdrop click and Esc
   */
  handleClose: () => void;
  /**
   * The file to preview; `filePath` is already a resolved signed URL or a local blob URL
   */
  file: FilePath;
  /**
   * Names the frame for assistive technology, and is the image's alt text
   */
  title?: string;
  /**
   * Translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
}
export const FilePreview = ({ isSmallDevice, handleClose, file, title, t }: FilePreviewProps): JSX.Element => {
  const [full, setFull] = useState(false);
  const [previewFailed, setPreviewFailed] = useState(false);
  const hasLoaded = useRef(false);
  const handleImageClick = () => {
    if (!isSmallDevice) setFull(!full);
  };
  // A PDF can never render in an <img>, so it goes to the browser's own viewer instead. Every other type keeps
  // the image element it has always used — including the zoom toggle, which a PDF viewer provides itself.
  const isPdf = getFileDetails(file?.filePath).type === AllowedFileTypes.PDF;
  const fileName = getPreviewFileName(file?.filePath);
  const documentHeight = isSmallDevice ? DOCUMENT_HEIGHT_SMALL : DOCUMENT_HEIGHT;
  useEffect(() => {
    if (!isPdf) return;
    const timer = setTimeout(() => {
      if (!hasLoaded.current) setPreviewFailed(true);
    }, PREVIEW_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isPdf, file?.filePath]);
  const openInNewTabLabel = t('ns.common:file_preview:open_in_new_tab_button');
  /**
   * The header action, and the same action again inside the fallback. Only the header one carries the testid,
   * so a caller looking it up never finds two.
   */
  const renderOpenInNewTab = (dataTestId?: string) =>
    isSmallDevice ? (
      <IconButton
        data-testid={dataTestId}
        component="a"
        href={file?.filePath}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={openInNewTabLabel}
        sx={{
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          color: (theme: Theme) => theme.palette.primary.main,
        }}
      >
        <ExternalLink size={16} absoluteStrokeWidth strokeWidth={1.5} />
      </IconButton>
    ) : (
      <Button
        data-testid={dataTestId}
        component="a"
        href={file?.filePath}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        variant="contained"
        color="primary"
        startIcon={<ExternalLink size={16} absoluteStrokeWidth strokeWidth={1.5} />}
      >
        {openInNewTabLabel}
      </Button>
    );
  return (
    <Modal
      open
      onClose={handleClose}
      data-testid="file-preview"
      slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0, 0, 0, 0.3)' } } }}
    >
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isSmallDevice ? '16px' : '32px',
          // Lets a click outside the card reach the backdrop underneath, which is what closes the modal
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            pointerEvents: 'auto',
            width: '100%',
            maxWidth: '760px',
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: (theme) => theme.palette.background.paper,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0px 12px 32px rgba(16, 24, 40, 0.18)',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              padding: '14px 16px',
              borderBottom: (theme) => `1px solid ${theme.custom.colours?.grey200}`,
            }}
          >
            <Typography
              title={fileName}
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: 600,
                fontSize: '15px',
                lineHeight: 1.3,
                color: (theme) => theme.custom.colours?.grey900,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {fileName}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 'none' }}>
              {renderOpenInNewTab('download-button')}

              <Box
                sx={{
                  width: '1px',
                  height: '24px',
                  margin: '0 2px',
                  backgroundColor: (theme) => theme.custom.colours?.grey200,
                }}
              />

              <IconButton
                onClick={handleClose}
                aria-label={t('ns.common:modal:close_button:aria_label')}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: (theme: Theme) => theme.custom.colours?.grey900,
                  opacity: 0.6,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: (theme: Theme) => theme.custom.colours?.grey100,
                  },
                }}
              >
                <X size={16} absoluteStrokeWidth strokeWidth={1.5} />
              </IconButton>
            </Box>
          </Box>

          {isPdf ? (
            <Box
              sx={{
                height: documentHeight,
                // Lets the region give way on a short viewport instead of pushing the card past the screen
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: (theme) => theme.custom.colours?.grey100,
              }}
            >
              {previewFailed ? (
                <Box
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    padding: '16px',
                    textAlign: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontSize: '13px',
                      lineHeight: 1.4,
                      color: (theme) => theme.custom.colours?.grey500,
                    }}
                  >
                    {t('ns.common:file_preview:error:not_previewable')}
                  </Typography>
                  {renderOpenInNewTab()}
                </Box>
              ) : (
                <Box
                  component="iframe"
                  src={`${file.filePath}${VIEWER_CHROME_FRAGMENT}`}
                  // Falls back to the path so the frame is always named for assistive technology — most callers
                  // pass no title
                  title={title ?? file.filePath}
                  onLoad={() => {
                    hasLoaded.current = true;
                  }}
                  onError={() => setPreviewFailed(true)}
                  sx={{ display: 'block', width: '100%', height: '100%', border: 'none' }}
                />
              )}
            </Box>
          ) : (
            <Box
              onClick={handleImageClick}
              onKeyPress={handleImageClick}
              role="button"
              tabIndex={0}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: IMAGE_HEIGHT,
                // Lets the region give way on a short viewport instead of pushing the card past the screen
                minHeight: 0,
                backgroundColor: (theme) => theme.custom.colours?.grey100,
                // Zoomed in, the image overflows the region and is scrolled rather than resizing the card
                overflow: full ? 'auto' : 'hidden',
              }}
            >
              <img
                src={file.filePath}
                alt={title ?? fileName}
                style={
                  full
                    ? { cursor: 'zoom-out' }
                    : { cursor: 'zoom-in', height: '100%', width: '100%', objectFit: 'contain' }
                }
              />
            </Box>
          )}

          {isPdf && !previewFailed && (
            <Box
              sx={{
                flexShrink: 0,
                padding: '12px 16px',
                backgroundColor: (theme) => theme.custom.colours?.grey50,
                borderTop: (theme) => `1px solid ${theme.custom.colours?.grey200}`,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 400,
                  fontSize: '13px',
                  lineHeight: 1.4,
                  color: (theme) => theme.custom.colours?.grey500,
                }}
              >
                {t('ns.common:file_preview:helper_text')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};
